import { useEffect, useState } from 'react';

/** Matches the `p-6` on `.booking-block`. */
const BLOCK_PADDING = 24;

/** Below this the band only fits a sliver, so the image is dropped instead. */
export const MIN_IMAGE_BAND = 24;

/**
 * Height in pixels of the free band between the bottom of the booking text block
 * and the bottom of the module, inside the block's padding. The booking image is
 * sized to this so it can never cover the text.
 *
 * Measured in the block's own layout frame (`clientHeight` / `offsetTop` /
 * `offsetHeight`) rather than `getBoundingClientRect()`, because every page renders
 * inside a rotation transform and `getBoundingClientRect()` reports axis-aligned
 * viewport coordinates that no longer correspond to the element's local edges once
 * rotated. `offsetTop` ignores transforms, so the `translate(...)` applied to the
 * text block for pixel-offset settings is invisible to it — `offsetY` is passed in
 * separately so it can be added back.
 *
 * `layoutKey` must change whenever a setting moves the text block without resizing
 * it (alignment, pixel offsets, hidden rows, theme font sizes) — ResizeObserver
 * does not fire for those.
 */
export function useBookingImageBand(
  block: HTMLDivElement | null,
  text: HTMLDivElement | null,
  offsetY: number,
  layoutKey: string,
) {
  const [band, setBand] = useState(0);

  useEffect(() => {
    const blockEl = block;
    const textEl = text;

    if (!blockEl || !textEl) {
      return;
    }

    const measure = () => {
      setBand(Math.max(0, Math.floor(blockEl.clientHeight - BLOCK_PADDING - (textEl.offsetTop + textEl.offsetHeight + offsetY))));
    };

    measure();

    const frame = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);

    // The display app builds for chrome >= 47; ResizeObserver is Chrome 64+.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    observer?.observe(blockEl);
    observer?.observe(textEl);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [block, text, offsetY, layoutKey]);

  return band;
}
